import { Center, HStack, Text } from '@chakra-ui/react';
import AppRoutes from 'config/AppRoutes';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { SummaryFormIcon } from 'ui/icons/Icons';
import PageLayout from 'ui/layout/PageLayout';
import RoundedCard from 'ui/molecules/RoundedCard';
import SMEContactForm from 'components/SMEContactForm/SMEContactForm';
import Button from 'ui/molecules/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import useConsumptionConfig from 'pages/PageConsumption/hooks/useConsumptionConfig';
import useBillConfig from 'pages/PageBill/hooks/useBillConfig';
import { formatNumber } from 'utils/formatNumber';
import { FormContactSMESchema } from 'components/SMEContactForm/hooks/useFormContactSMESchema';
import { createLead } from 'api';
import { getClientDetails } from 'utils/getClientDetails';
import { useSelector } from 'react-redux';
import { AnswersSelector } from 'store/selectors';
import { useVendorConfig } from 'store/vendor/useVendorConfig';
import getFirstExistingTranslation from 'utils/getFirstExistingTranslation';

export enum SMEContactReason {
  EUR = 'eur',
  KW = 'kw',
}

const PageSMEContact = () => {
  const { t, i18n } = useTranslation();
  const answers = useSelector(AnswersSelector);
  const vendorConfig = useVendorConfig();

  const sendFormMutation = useMutation(createLead);
  const isSubmitting = sendFormMutation.isLoading;
  const consumptionConfig = useConsumptionConfig();
  const billConfig = useBillConfig();
  const navigate = useNavigate();
  const { search } = useLocation();
  const contactReason = new URLSearchParams(search).get(
    'contactReason'
  ) as SMEContactReason;

  const onSubmit = async (data: FormContactSMESchema) => {
    const clientDetails = getClientDetails(
      data,
      answers,
      vendorConfig,
      'SME High Consumption client'
    );

    sendFormMutation.mutate(
      // we override the origin to add the B2B mention for the SME contact form
      { ...clientDetails, origin: `${vendorConfig.origin} B2B` },
      {
        onSuccess: () => {
          navigate(AppRoutes.THANKS);
        },
      }
    );
  };

  const handleBack = () => {
    // when in the SME contact, we want to go back to the consumption page
    navigate(AppRoutes.CONSUMPTION);
  };

  return (
    <PageLayout>
      <RoundedCard height="full" p={{ sm: 6, md: 10 }} mt={{ sm: 2, md: 6 }}>
        <HStack paddingBottom="15px">
          <SummaryFormIcon alignSelf="start" top={1} position="relative" />
          <Text fontSize={{ sm: '14px', md: '16px' }} paddingLeft="5px">
            <Trans
              i18nKey={getFirstExistingTranslation(t, i18n, [
                `SME_CONTACT_title_${contactReason}`,
                'common.error.description',
              ])}
              components={{ bold: <b /> }}
              values={{
                amount_kw: formatNumber(consumptionConfig.max),
                amount_euro: formatNumber(billConfig.max),
              }}
            />
          </Text>
        </HStack>
        <SMEContactForm
          isSubmitting={isSubmitting}
          submitElement={
            <Center pt="15px">
              <Button
                type="submit"
                whiteSpace="normal"
                wordBreak="break-word"
                isLoading={isSubmitting}
                width={{ base: '80%', md: '50%' }}
              >
                {t('SME_CONTACT.form.submit')}
              </Button>
            </Center>
          }
          onSubmit={onSubmit}
        />
      </RoundedCard>
      <Center>
        <Button variant="backButton" onClick={handleBack}>
          {t('common.backButton')}
        </Button>
      </Center>
    </PageLayout>
  );
};

export default PageSMEContact;
